import React, { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import './Operation.css';
import PowerEnergyCharts from '../SingleScenario/Plots';
import STMMap from '../Map/STMMap';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Operation() {
    const { userId, experimentId, scenarioId } = useParams();
    const [scenarioData, setScenarioData] = useState(null);
    const [outputData, setOutputData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError(null);

            try {

                const scenarioUrl = `${API_BASE_URL}/users/${userId}/experiments/${experimentId}/${scenarioId}/scenario`;
                const resultsUrl = `${API_BASE_URL}/users/${userId}/experiments/${experimentId}/${scenarioId}/results`;


                const [scenarioResponse, outputResponse] = await Promise.all([
                    fetch(scenarioUrl),
                    fetch(resultsUrl)
                ]);

                if (!scenarioResponse.ok) {
                    throw new Error(`Server returned ${scenarioResponse.status} for scenario`);
                }
                if (!outputResponse.ok) {
                    throw new Error(`Server returned ${outputResponse.status} for results`);
                }

                const [scenarioData, outputData] = await Promise.all([
                    scenarioResponse.json(),
                    outputResponse.json()
                ]);

                setScenarioData(scenarioData);
                setOutputData(outputData);
                setLoading(false);
            } catch (fetchError) {
                console.error("Fetch failed:", fetchError);
                setError(fetchError.message);
                setLoading(false);
            }
        }
        fetchData();
    }, [userId, experimentId, scenarioId]);

    if (loading) {
        return <div className="loading-container">Loading scenario...</div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <h2>Error loading scenario</h2>
                <p>{error}</p>
                <p>Please check the URL and try again.</p>
            </div>
        );
    }

    if (!scenarioData) {
        return (
            <div className="no-data-container">
                <h2>No data available</h2>
                <p>No data available for scenario {scenarioId} in experiment {experimentId}.</p>
            </div>
        );
    }
    const totalRoutes = Array.isArray(scenarioData.routes)
        ? scenarioData.routes.length
        : 0;

    const totalVehicles = Array.isArray(scenarioData.vehicles)
        ? scenarioData.vehicles.reduce((sum, v) => sum + (v.count || 0), 0)
        : 0;

    const totalChargers = Array.isArray(scenarioData.chargers)
        ? scenarioData.chargers.reduce((sum, c) => sum + (c.count || 0), 0)
        : 0;

    return (
        <div className="specific-scenario">
            <div className="heading-title">
                <h1>Operation</h1>
                <h4>{experimentId}: {scenarioId}</h4>
            </div>
            <div className="overview-container">

                <div className="metrics-container">
                    <div className="metric-box">
                        <p>
                            <strong className="metric-value">{totalVehicles}</strong>{" "}
                            <span className="metric-label">Vehicles</span>
                        </p>
                    </div>
                    <div className="metric-box">
                        <p>
                            <strong className="metric-value">{totalChargers}</strong>{" "}
                            <span className="metric-label">Chargers</span>
                        </p>
                    </div>
                    <div className="metric-box">
                        <p>
                            <strong className="metric-value">{totalRoutes}</strong>{" "}
                            <span className="metric-label">Routes</span>
                        </p>
                    </div>
                    {outputData && outputData.metadata && (
                        <>
                            <div className="metric-box">
                                <p>
                                    <span className="metric-label">Peak Power: </span>
                                    <strong className="metric-value">{outputData.metadata.peak_power_kW.toFixed(2)}</strong>{" "}kW
                                </p>
                            </div>
                            <div className="metric-box">
                                <p>
                                    <span className="metric-label">Electricity Purchased: </span>
                                    <strong className="metric-value">{outputData.metadata.electricity_purchased_kWh.toFixed(2)}</strong>{" "}kWh
                                </p>
                            </div>
                            <div className="metric-box">
                                <p>
                                    <span className="metric-label">Total Price: </span>
                                    <strong className="metric-value">{(outputData.metadata.total_price.toFixed(2))}</strong>{" "}$
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div className="charts-section">
                {!loading && scenarioData && outputData && (
                    <PowerEnergyCharts
                        userId={userId}
                        experimentId={experimentId}
                        scenarioId={scenarioId}
                        key={`plots-${experimentId}-${scenarioId}`}
                    />
                )}
            </div>
            <div className="map-and-table-section">
                <div>
                    <h1>Montreal STM Routes</h1>
                    <STMMap />
                </div>
                <div className="tables-section">
                    <div className="table-container">
                        <h2 className="table-header">Routes Summary</h2>
                        <table className="fleet-table">
                            <thead>
                                <tr>
                                    <th>Route ID</th>
                                    <th>Start Time</th>
                                    <th>Stop Time</th>
                                    <th>Number of Vehicles</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scenarioData.routes &&
                                    scenarioData.routes.map((route, index) => {
                                        // Extract only the time part (HH:MM) from ISO date strings
                                        const formatTime = (isoString) => {
                                            try {
                                                // Extract time portion (assumes format like "1900-01-01T07:30:00")
                                                const timePart = isoString.split('T')[1];
                                                // Take just the HH:MM portion
                                                return timePart ? timePart.substring(0, 5) : isoString;
                                            } catch (e) {
                                                return isoString;
                                            }
                                        };

                                        return (
                                            <tr key={index}>
                                                <td>{route.id}</td>
                                                <td>{formatTime(route.start_time)}</td>
                                                <td>{formatTime(route.stop_time)}</td>
                                                <td>{route.vehicles}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>

                    </div>
                </div>
            </div>
        </div>

    );
}
